import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import { mainCategoryData, hingeOptions, handleOptions } from "../data";
import { fetchQuotationById, updateQuotation } from "../api";
import "./NewQuotationPage.css";

/**
 * EditQuotationPage.jsx
 * - Merged version with Percentage / Multiplier popup integrated.
 * - Preserves server-loaded materials/items (won't overwrite during initial load).
 * - Saves multiplier to backend in the payload as `multiplier`.
 */

const getFinancialYear = () => {
  const d = new Date();
  const m = d.getMonth();
  let startYear = d.getFullYear();
  if (m >= 0 && m <= 2) startYear -= 1;
  return startYear;
};

export default function EditQuotationPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const currentFY = getFinancialYear();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Multiplier states
  const [showMultiplierTable, setShowMultiplierTable] = useState(false);
  const [selectedMultiplier, setSelectedMultiplier] = useState(1);

  // data states
  const [formData, setFormData] = useState({
    quotationNo: "",
    date: new Date().toISOString().split("T")[0],
    companyName: "",
    address: "",
    gstin: "",
    contactName: "",
    contactNo: "",
    productName: "",
    mainCategory: "C3.3",
    hingeType: hingeOptions[0] || "Hinge Small",
    handleType: handleOptions[0] || "Surface Handle - small",
    latchType: "",
    caseColorType: "Silver",
    caseLength: 0,
    caseWidth: 0,
    caseTopHeight: 0,
    caseBottomHeight: 0,
    caseQuantity: 1,
    subtotal: 0,
    grandTotal: 0,
    financialYear: currentFY,
  });

  const [materials, setMaterials] = useState([]);
  const [items, setItems] = useState([]);

  // keep an initialLoad flag so we don't overwrite server data during initial setFormData
  const initialLoadRef = useRef(true);

  // ------------- load quote -------------
  useEffect(() => {
    const load = async () => {
      if (!id) {
        setLoading(false);
        initialLoadRef.current = false;
        return;
      }

      try {
        const q = await fetchQuotationById(id);
        if (!q) throw new Error("Quotation not found");

        // Accept either flat response or attributes wrapper
        const data = q.attributes && typeof q.attributes === "object" ? q.attributes : q;

        setFormData((prev) => ({
          ...prev,
          quotationNo: data.quotationNo || data.documentNo || prev.quotationNo || "",
          date: data.date || prev.date,
          companyName: data.companyName || "",
          address: data.address || "",
          gstin: data.gstin || "",
          contactName: data.contactName || "",
          contactNo: data.contactNo || "",
          productName: data.productName || "",
          mainCategory: data.mainCategory || prev.mainCategory,
          hingeType: data.hingeType || prev.hingeType,
          handleType: data.handleType || prev.handleType,
          latchType: data.latchType || prev.latchType || "",
          caseColorType: data.caseColorType || prev.caseColorType || "Silver",
          caseLength: Number(data.caseLength) || 0,
          caseWidth: Number(data.caseWidth) || 0,
          caseTopHeight: Number(data.caseTopHeight) || 0,
          caseBottomHeight: Number(data.caseBottomHeight) || 0,
          caseQuantity: Number(data.caseQuantity) || 1,
          subtotal: Number(data.subtotal) || 0,
          grandTotal: Number(data.grandTotal) || 0,
          financialYear: data.financialYear || currentFY,
        }));

        // load materials/items if present on server, otherwise they'll be auto-calculated
        setMaterials((q.materials && q.materials.length) ? q.materials : (data.materials || []));
        setItems((q.items && q.items.length) ? q.items : (data.items || []));

        // load multiplier if stored on server
        setSelectedMultiplier(Number(data.multiplier) || 1);
      } catch (err) {
        console.error("Error loading quotation:", err);
        alert("Failed to load quotation: " + (err.message || err));
      } finally {
        setLoading(false);
        initialLoadRef.current = false;
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentFY]);

  // ------------- materials calculation -------------
  useEffect(() => {
    // skip auto-calc while loading initial server values (so we don't overwrite saved materials)
    if (loading || initialLoadRef.current) return;

    const { caseLength, caseWidth, caseTopHeight, caseBottomHeight, mainCategory } = formData;
    const newMaterials = [];

    if (mainCategory === "C3.3" || mainCategory === "C5.5") {
      const offset = mainCategory === "C3.3" ? -15 : +11;
      const mdfLength = caseLength + offset;
      const mdfWidth = caseWidth + offset;
      const mdfTopHeight = caseTopHeight + (mainCategory === "C3.3" ? -7.5 : 0);
      const mdfBottomHeight = caseBottomHeight + (mainCategory === "C3.3" ? -7.5 : 0);

      newMaterials.push({
        name: "MDF",
        value:
          (2 *
            (mdfLength * mdfWidth +
              mdfLength * mdfTopHeight +
              mdfLength * mdfBottomHeight +
              mdfWidth * mdfTopHeight +
              mdfWidth * mdfBottomHeight)) / 1_000_000,
        wastage: 30,
        wastageValue: 0,
      });

      newMaterials.push({
        name: "Eva Sheets",
        value:
          (caseLength * caseWidth * (caseTopHeight + caseBottomHeight)) / 1_000_000_000,
        wastage: 20,
        wastageValue: 0,
      });

      const lAngleLength = caseLength - (mainCategory === "C3.3" ? 28 : 30);
      const lAngleWidth = caseWidth - (mainCategory === "C3.3" ? 28 : 30);
      const lAngleTopHeight = caseTopHeight + (mainCategory === "C3.3" ? -8 : 3);
      const lAngleBottomHeight = caseBottomHeight + (mainCategory === "C3.3" ? -8 : 3);

      newMaterials.push({
        name: "Aluminium L Angle",
        value: (4 * (lAngleLength + lAngleWidth + lAngleTopHeight + lAngleBottomHeight)) / 1000,
        wastage: 20,
        wastageValue: 0,
      });

      const tongueLength = caseLength - (mainCategory === "C3.3" ? 28 : 27);
      const tongueWidth = caseWidth - (mainCategory === "C3.3" ? 28 : 27);
      newMaterials.push({
        name: "Aluminium Tongue & Groove",
        value: (2 * (tongueLength + tongueWidth)) / 1000,
        wastage: 20,
        wastageValue: 0,
      });
    }

    newMaterials.forEach((m) => {
      m.wastageValue = parseFloat((m.value * (1 + m.wastage / 100)).toFixed(4));
    });

    setMaterials(newMaterials);
  }, [
    loading,
    formData.caseLength,
    formData.caseWidth,
    formData.caseTopHeight,
    formData.caseBottomHeight,
    formData.mainCategory,
  ]);

  // ------------- items calculation -------------
  useEffect(() => {
    if (loading || initialLoadRef.current) return;

    const baseItems = mainCategoryData[formData.mainCategory] || [];
    const newItems = [];
    const labourItems = [];

    const getWastageQuantity = (name) =>
      materials.find((m) => m.name === name)?.wastageValue || 0;

    const map = {
      "Aluminium Profile Round Angle 3.3": "Aluminium L Angle",
      "Aluminium Profile Square Angle 5.5": "Aluminium L Angle",
      "Aluminium Profile Tongue & Groove 3.3": "Aluminium Tongue & Groove",
      "Aluminium Profile Tongue & Groove 5.5": "Aluminium Tongue & Groove",
      "MDF 3.3": "MDF",
      "MDF 5.5": "MDF",
      "Eva Sheets": "Eva Sheets",
    };

    const fixedQuantities = {
      "Corner 3.3": 8,
      "Corner 5.5": 8,
      "Corner Protector 3.3- Tongue & Groove": 4,
      "Corner Protector 5.5- Tongue & Groove": 4,
      Hinge: 1,
      Latch: 2,
      Rivet: 80,
      Handle: 1,
      "Rubber Feet": 4,
      "Plastic Feet": 4,
      Labour: 1,
    };

    const getUnit = (name) => {
      if (
        [
          "Aluminium Profile Round Angle 3.3",
          "Aluminium Profile Square Angle 5.5",
          "Aluminium Profile Tongue & Groove 3.3",
          "Aluminium Profile Tongue & Groove 5.5",
          "Anodizing",
        ].includes(name)
      )
        return "m";
      if (["MDF 3.3", "MDF 5.5"].includes(name)) return "m²";
      if (name === "Eva Sheets") return "m³";
      return "Nos";
    };

    baseItems.forEach((item) => {
      // hinge/handle selection filters
      if (item.name.toLowerCase().includes("hinge") && item.name !== formData.hingeType) return;
      if (item.name.toLowerCase().includes("handle") && item.name !== formData.handleType) return;

      // latch: if user selected a latchType, include only that latch item; otherwise include default behavior
      if (item.name.toLowerCase().includes("latch")) {
        if (formData.latchType && item.name !== formData.latchType) return;
        if (!formData.latchType) {
          // no specific latch selected: allow base item to be included with fixedQty logic below
        }
      }

      let qty = 0;
      if (map[item.name]) qty = getWastageQuantity(map[item.name]);
      else {
        for (const [key, fixedQty] of Object.entries(fixedQuantities)) {
          if (item.name.toLowerCase().includes(key.toLowerCase())) qty = fixedQty;
        }
      }
      if (qty === 0) qty = 1;

      const unit = getUnit(item.name);
      const newItem = {
        ...item,
        unit,
        quantity: parseFloat(qty.toFixed(4)),
        price: item.price || 0,
        amount: parseFloat(((qty || 0) * (item.price || 0)).toFixed(2)),
        category: formData.mainCategory,
      };

      if (item.name.toLowerCase().includes("labour")) labourItems.push(newItem);
      else newItems.push(newItem);
    });

    // add anodizing when black color selected
    if (formData.caseColorType === "Black") {
      const anodizingQty =
        getWastageQuantity("Aluminium L Angle") + getWastageQuantity("Aluminium Tongue & Groove");
      const anodizingPrice = 17;
      if (anodizingQty > 0) {
        newItems.push({
          category: formData.mainCategory,
          name: "Anodizing",
          unit: "m",
          quantity: parseFloat(anodizingQty.toFixed(4)),
          price: anodizingPrice,
          amount: parseFloat((anodizingQty * anodizingPrice).toFixed(2)),
        });
      }
    }

    setItems([...newItems, ...labourItems]);
  }, [
    loading,
    formData.mainCategory,
    formData.hingeType,
    formData.handleType,
    formData.latchType,
    formData.caseColorType,
    materials,
  ]);

  // ------------- subtotal & grand total -------------
  useEffect(() => {
    const subtotal = items.reduce((s, i) => s + (i.amount || 0), 0);
    const qty = formData.caseQuantity || 1;
    const total = subtotal * qty;

    // update only subtotal & grandTotal to avoid re-triggering items/materials effects
    setFormData((prev) => ({
      ...prev,
      subtotal: parseFloat(subtotal.toFixed(2)),
      grandTotal: parseFloat(total.toFixed(2)),
    }));
  }, [items, formData.caseQuantity]);

  // ------------- handlers -------------
  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleMaterialWastageChange = (index, e) => {
    const value = parseFloat(e.target.value) || 0;
    const updated = [...materials];
    updated[index].wastage = value;
    updated[index].wastageValue = parseFloat((updated[index].value * (1 + value / 100)).toFixed(4));
    setMaterials(updated);
  };

  const handleItemChange = (idx, field, raw) => {
    const updated = [...items];
    const value = field === "name" ? raw : parseFloat(raw) || 0;
    updated[idx][field] = value;

    // keep amount consistent
    updated[idx].amount = parseFloat(((updated[idx].quantity || 0) * (updated[idx].price || 0)).toFixed(2));
    setItems(updated);
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        category: formData.mainCategory,
        name: "",
        quantity: 0,
        price: 0,
        amount: 0,
        unit: "Nos",
      },
    ]);
  };

  // ------------- Multiplier Logic -------------
  const handleApplyMultiplier = () => {
    // Copy items and apply multiplier. For raw material names, multiply quantity; for others multiply price.
    const excludeList = [
      "MDF",
      "Eva Sheets",
      "Aluminium L Angle",
      "Aluminium Tongue & Groove",
    ];

    const updatedItems = items.map((item) => {
      // defensive defaults
      const itemName = item.name || "";
      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;

      if (excludeList.includes(itemName)) {
        const newQty = qty * selectedMultiplier;
        const newAmount = parseFloat((newQty * price).toFixed(2));
        return {
          ...item,
          quantity: parseFloat(newQty.toFixed(4)),
          amount: newAmount,
        };
      } else {
        const newPrice = price * selectedMultiplier;
        const newAmount = parseFloat((qty * newPrice).toFixed(2));
        return {
          ...item,
          price: parseFloat(newPrice.toFixed(2)),
          amount: newAmount,
        };
      }
    });

    setItems(updatedItems);

    // update subtotal & grandTotal immediately
    const newSubtotal = updatedItems.reduce((s, i) => s + (i.amount || 0), 0);
    const qty = formData.caseQuantity || 1;
    setFormData((prev) => ({
      ...prev,
      subtotal: parseFloat(newSubtotal.toFixed(2)),
      grandTotal: parseFloat((newSubtotal * qty).toFixed(2)),
    }));
  };

  // ------------- save -------------
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.productName) {
      alert("Company Name and Product Name are required.");
      return;
    }

    setSaving(true);

    try {
      const subtotal = items.reduce((s, i) => s + (i.amount || 0), 0);
      const qty = formData.caseQuantity || 1;
      const finalTotal = subtotal * qty;

      const payload = {
        ...formData,
        subtotal: parseFloat(subtotal.toFixed(2)),
        caseQuantity: qty,
        grandTotal: parseFloat(finalTotal.toFixed(2)),
        materials: materials.map((m) => ({
          name: m.name,
          value: m.value,
          wastage: m.wastage,
          wastageValue: m.wastageValue,
        })),
        items: items.map((it) => ({
          category: it.category,
          name: it.name,
          quantity: it.quantity,
          unit: it.unit,
          price: it.price,
          amount: it.amount,
        })),
        multiplier: selectedMultiplier, // persist multiplier
      };

      await updateQuotation(id, payload);
      alert("Quotation updated successfully!");
      navigate("/list-quotation");
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving quotation: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Header />
      <div className="new-quotation-container">
        <h1>Edit Quotation</h1>

        <form onSubmit={handleSave}>
          {/* Top Info */}
          <div className="quotation-info single-row">
            <div className="info-item">
              <label>Quotation No:</label>
              <input type="text" value={formData.quotationNo} readOnly />
            </div>
            <div className="info-item">
              <label>Financial Year:</label>
              <input type="number" value={formData.financialYear} readOnly />
            </div>
            <div className="info-item">
              <label>Date:</label>
              <input type="text" value={formData.date} readOnly />
            </div>
          </div>

          {/* Customer Details Section */}
          <div className="customer-details">
            <h3>Customer Details</h3>
            <div className="quotation-info">
              <div className="info-item">
                <label>Company Name:</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, companyName: e.target.value }))}
                />
              </div>

              <div className="info-item">
                <label>Product Name:</label>
                <input
                  type="text"
                  value={formData.productName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, productName: e.target.value }))}
                />
              </div>

              <div className="info-item">
                <label>GSTIN:</label>
                <input
                  type="text"
                  value={formData.gstin}
                  maxLength={15}
                  onChange={(e) => {
                    const gst = e.target.value.toUpperCase().slice(0, 15);
                    setFormData((prev) => ({ ...prev, gstin: gst }));
                  }}
                />
              </div>

              <div className="info-item">
                <label>Contact Name:</label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, contactName: e.target.value }))}
                />
              </div>
              <div className="info-item">
                <label>Contact No:</label>
                <input
                  type="text"
                  value={formData.contactNo}
                  onChange={(e) => setFormData((prev) => ({ ...prev, contactNo: e.target.value }))}
                />
              </div>

              <div className="info-item">
                <label>Address:</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Dropdowns */}
          <div className="quotation-info single-row">
            <div className="info-item">
              <label>Main Category:</label>
              <select
                value={formData.mainCategory}
                onChange={(e) => setFormData((prev) => ({ ...prev, mainCategory: e.target.value }))}
              >
                <option value="C3.3">3.3</option>
                <option value="C5.5">5.5</option>
              </select>
            </div>
            <div className="info-item">
              <label>Hinge Type:</label>
              <select
                value={formData.hingeType}
                onChange={(e) => setFormData((prev) => ({ ...prev, hingeType: e.target.value }))}
              >
                {hingeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="info-item">
              <label>Handle Type:</label>
              <select
                value={formData.handleType}
                onChange={(e) => setFormData((prev) => ({ ...prev, handleType: e.target.value }))}
              >
                {handleOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="info-item">
              <label>Latch Type:</label>
              <select
                value={formData.latchType || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, latchType: e.target.value }))}
              >
                <option value="">--Select Latch--</option>
                {mainCategoryData[formData.mainCategory]
                  .filter((item) => item.name.toLowerCase().includes("latch"))
                  .map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="info-item">
              <label>Case Color:</label>
              <select
                name="caseColorType"
                value={formData.caseColorType}
                onChange={(e) => setFormData((prev) => ({ ...prev, caseColorType: e.target.value }))}
              >
                <option value="">Select Color</option>
                <option value="Silver">Silver</option>
                <option value="Black">Black</option>
              </select>
            </div>
          </div>

          {/* Dimensions */}
          <div className="case-dimensions">
            <h3>Case Dimensions (mm)</h3>
            <div className="dimension-inputs">
              {["caseLength", "caseWidth", "caseTopHeight", "caseBottomHeight"].map((key) => (
                <div className="dimension-item" key={key}>
                  <label>{key.replace(/case/, "").replace(/([A-Z])/g, " $1")}: </label>
                  <input type="number" name={key} value={formData[key]} onChange={handleDimensionChange} />
                </div>
              ))}
            </div>
          </div>

          {/* Materials Table */}
          <table className="material-table vertical-only">
            <thead>
              <tr>
                <th>Material</th>
                <th>Value</th>
                <th>Wastage %</th>
                <th>With Wastage</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m, i) => {
                let unit = "";
                if (m.name === "MDF") unit = "m²";
                else if (m.name === "Eva Sheets") unit = "m³";
                else if (m.name === "Aluminium L Angle" || m.name === "Aluminium Tongue & Groove")
                  unit = "m";

                return (
                  <tr key={i}>
                    <td>{m.name}</td>
                    <td>
                      {m.value?.toFixed(4)} {unit}
                    </td>
                    <td>
                      <input type="number" value={m.wastage} onChange={(e) => handleMaterialWastageChange(i, e)} />
                    </td>
                    <td>
                      {(m.wastageValue ?? 0).toFixed(4)} {unit}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Items Table */}
          <table className="item-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>
                    <input
                      className="item-input"
                      value={it.name}
                      placeholder="Enter item name"
                      onChange={(e) => handleItemChange(i, "name", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={it.quantity}
                      onChange={(e) => handleItemChange(i, "quantity", e.target.value)}
                    />
                  </td>
                  <td>{it.unit || "Nos"}</td>
                  <td>
                    <input
                      type="number"
                      value={it.price}
                      onChange={(e) => handleItemChange(i, "price", e.target.value)}
                    />
                  </td>
                  <td>₹{(it.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button type="button" className="add-row-btn" onClick={handleAddItem}>
            ➕ Add Row
          </button>

          {/* Summary Box aligned below Amount column */}
          <div className="summary-below">
            <h3>Summary</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td>Per Unit Price</td>
                  <td style={{ textAlign: "right" }}>₹{(formData.subtotal || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td> Quantity</td>
                  <td style={{ textAlign: "right" }}>
                    <input
                      type="number"
                      min="1"
                      value={formData.caseQuantity || 1}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          caseQuantity: parseFloat(e.target.value) || 1,
                        }))
                      }
                      style={{
                        width: "80px",
                        textAlign: "right",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "3px",
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Taxable Amount</strong>
                  </td>
                  <td style={{ textAlign: "right", fontWeight: "bold" }}>
                    ₹{(formData.grandTotal || 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ---------------- Multiplier / Percentage UI ---------------- */}
          <div className="grand-total-section" style={{ marginTop: 12 }}>
            <div className="percentage-control">
              <button
                type="button"
                className="percentage-button"
                onClick={() => setShowMultiplierTable(!showMultiplierTable)}
              >
                Percentage ({((selectedMultiplier - 1) * 100).toFixed(0)}%)
              </button>

              {showMultiplierTable && (
                <div className="multiplier-popup" style={{ marginTop: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left" }}>Select</th>
                        <th style={{ textAlign: "left" }}>Multiplier</th>
                        <th style={{ textAlign: "right" }}>Calculated Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 7.5, 10].map((m) => (
                        <tr key={m}>
                          <td>
                            <input
                              type="radio"
                              name="multiplierSelect"
                              value={m}
                              checked={selectedMultiplier === m}
                              onChange={(e) => setSelectedMultiplier(parseFloat(e.target.value))}
                            />
                          </td>
                          <td>{m}</td>
                          <td style={{ textAlign: "right" }}>{(formData.subtotal * m).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="popup-buttons" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={() => {
                        handleApplyMultiplier();
                        setShowMultiplierTable(false);
                      }}
                      style={{ marginRight: 8 }}
                    >
                      OK
                    </button>
                    <button type="button" onClick={() => setShowMultiplierTable(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="grand-total" style={{ marginTop: 8 }}>
              <h3>
                Grand Total: ₹{(formData.grandTotal || 0).toFixed(2)}{" "}
                <span style={{ color: "#007bff", fontSize: "0.9em" }}>
                  ({((selectedMultiplier - 1) * 100).toFixed(0)}%)
                </span>
              </h3>
            </div>
          </div>

          {/* Save */}
          <div style={{ marginTop: 14 }}>
            <button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Update Quotation"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
